import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import UndoToast from '@/src/components/common/Toast/UndoToast';
import { useInbox } from '@/src/components/project/Inbox2/store/inbox.store';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import { showToast } from '@/src/globalFunction';
import { useToggle } from '@/src/hooks/useToggle';
import { toast } from '@/src/reducers/toast/toast.actions';

import ArchiveLibraryModal from './ArchiveLibraryModal/ArchiveLibraryModal';

import ActionButton from '../../../../../../Elements/ActionButton/ActionButton';
import { useMoveFile } from '../../../api/moveFile';
import { useFileEntryViewerSidebarActions } from '../../../store/fileEntryViewer.sidebarActions.store';

const INBOX_ROOT_FOLDER = {
  id: null,
  origin: null,
};

type ArchiveFileToLibraryProps = {
  fileEntry: FileEntry;
};

const ArchiveFileToLibrary = ({ fileEntry }: ArchiveFileToLibraryProps) => {
  const [showModal, toggleShowModal] = useToggle(false);
  const { projectId } = useParams<{ projectId: string }>();
  const moveFileMutation = useMoveFile({
    config: {
      onMutate: () => setIsMarkArchivedClicked(true),
      onSettled: () => setIsMarkArchivedClicked(false),
    },
  });
  const { sidebarActions } = useFileEntryViewerSidebarActions();
  const { onFileArchived } = sidebarActions;
  const dispatch = useDispatch();

  const { setIsMarkArchivedClicked } = useInbox();

  useEffect(() => {
    // Update the loading state for archiving action
    setIsMarkArchivedClicked(moveFileMutation.isLoading);
  }, [moveFileMutation.isLoading, setIsMarkArchivedClicked]);

  const handleMoveFile = async (newFolder) => {
    return moveFileMutation.mutateAsync({
      fileId: fileEntry.id,
      newFolderId: +newFolder.id,
    });
  };

  const handleUndo = async (sourceFolder, newFolderPath, tempBreadCrumbs) => {
    const destinationFolder = Object.keys(sourceFolder).length ? sourceFolder : INBOX_ROOT_FOLDER;

    const response = await handleMoveFile(destinationFolder);

    if (response) {
      onFileArchived({ destinationFolderId: destinationFolder.id, newFolderPath, tempBreadCrumbs });
      showToast('success', 'Action Undone!');
    }
  };

  const handleSubmit = async (destinationFolder, folderPath, tempBreadCrumbs) => {
    const response = await handleMoveFile(destinationFolder);

    if (response) {
      onFileArchived({
        destinationFolderId: destinationFolder.id,
        newFolderPath: folderPath,
        tempBreadCrumbs,
      });
      dispatch(
        toast(
          <UndoToast
            customClass={`inboxToast`}
            label={'Successfully archived entry.'}
            onUndo={() =>
              handleUndo(
                fileEntry.file.libraryLocation,
                fileEntry.file.libraryLocation.path,
                tempBreadCrumbs
              )
            }
          />
        )
      );
    }

    toggleShowModal();
  };

  // @TODO: transition check?
  useEffect(() => {
    toggleShowModal(false);
  }, [fileEntry.id, toggleShowModal]);

  return (
    <>
      <ActionButton
        id={'archive_document'}
        onClick={toggleShowModal}
        icon={ICONS.archive}
        tooltipSettings={{
          content: fileEntry.file.libraryLocation?.id ? 'View  in Library' : 'Archive to Library',
          place: 'left',
        }}
      />

      {showModal && (
        <ModalPortal>
          <ArchiveLibraryModal
            projectId={projectId}
            onClose={toggleShowModal}
            onSubmit={handleSubmit}
            fileEntry={fileEntry}
          />
        </ModalPortal>
      )}
    </>
  );
};

export default ArchiveFileToLibrary;
